<script setup>
// Fork JavaMifi: pengaturan tampilan widget live chat per inbox.
// Disimpan di channel_web_widgets.widget_theme. Kosong = tampilan bawaan Chatwoot.
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, default: () => ({}) },
});

const store = useStore();
const uiFlags = useMapGetter('inboxes/getUIFlags');

const FONTS = ['Inter', 'Poppins', 'Nunito', 'Plus Jakarta Sans', 'Roboto', 'DM Sans'];
const RADIUS = [
  { value: 'none', label: 'Kotak', px: '0px' },
  { value: 'sm', label: 'Sedikit', px: '6px' },
  { value: 'lg', label: 'Sedang', px: '12px' },
  { value: 'xl', label: 'Bulat', px: '20px' },
  { value: 'full', label: 'Sangat bulat', px: '28px' },
];
const HEADERS = [
  { value: 'expanded', label: 'Polos' },
  { value: 'banner', label: 'Banner berwarna' },
];
const BLOCKS = {
  greeting: 'Salam pembuka',
  quick_actions: 'Tombol cepat',
  start: 'Kartu mulai chat',
  articles: 'Artikel Help Center',
};
const GOOGLE_FONTS = {
  Poppins: 'Poppins:wght@400;500;600',
  Nunito: 'Nunito:wght@400;600;700',
  'Plus Jakarta Sans': 'Plus+Jakarta+Sans:wght@400;500;600',
  Roboto: 'Roboto:wght@400;500',
  'DM Sans': 'DM+Sans:wght@400;500',
};

const font = ref('Inter');
const radius = ref('lg');
const header = ref('expanded');
const startLabel = ref('');
const greeting = ref('');
const quickTitle = ref('');
// [{ key, enabled }] - urutan = urutan di halaman awal widget
const blocks = ref([]);
const quickActions = ref([]);

const setDefaults = () => {
  const theme = props.inbox.widget_theme || {};
  font.value = theme.font || 'Inter';
  radius.value = theme.radius || 'lg';
  header.value = theme.header || 'expanded';
  startLabel.value = theme.start_label || '';
  greeting.value = theme.greeting || '';
  quickTitle.value = theme.quick_title || '';
  const active = Array.isArray(theme.home_blocks) && theme.home_blocks.length
    ? theme.home_blocks
    : ['start', 'articles'];
  blocks.value = [
    ...active.filter(key => BLOCKS[key]).map(key => ({ key, enabled: true })),
    ...Object.keys(BLOCKS)
      .filter(key => !active.includes(key))
      .map(key => ({ key, enabled: false })),
  ];
  quickActions.value = (theme.quick_actions || []).map(a => ({
    label: a.label || '',
    message: a.message || '',
  }));
};

const moveBlock = (index, step) => {
  const target = index + step;
  if (target < 0 || target >= blocks.value.length) return;
  const list = [...blocks.value];
  [list[index], list[target]] = [list[target], list[index]];
  blocks.value = list;
};

const addQuickAction = () => {
  if (quickActions.value.length >= 6) return;
  quickActions.value.push({ label: '', message: '' });
};
const removeQuickAction = index => quickActions.value.splice(index, 1);

const radiusPx = computed(
  () => RADIUS.find(r => r.value === radius.value)?.px || '12px'
);
const widgetColor = computed(() => props.inbox.widget_color || '#1f93ff');
const previewBlocks = computed(() =>
  blocks.value.filter(b => b.enabled).map(b => b.key)
);
const filledActions = computed(() =>
  quickActions.value.filter(a => a.label.trim())
);

// Muat font pilihan supaya pratinjau tampil sesuai aslinya
watch(
  font,
  value => {
    const family = GOOGLE_FONTS[value];
    if (!family || document.getElementById(`wt-font-${value}`)) return;
    const link = document.createElement('link');
    link.id = `wt-font-${value}`;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${family}&display=swap`;
    document.head.appendChild(link);
  },
  { immediate: true }
);

const updateInbox = async () => {
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      channel: {
        widget_theme: {
          font: font.value,
          radius: radius.value,
          header: header.value,
          start_label: startLabel.value.trim(),
          greeting: greeting.value.trim(),
          quick_title: quickTitle.value.trim(),
          home_blocks: previewBlocks.value,
          quick_actions: filledActions.value.map(a => ({
            label: a.label.trim(),
            message: (a.message || a.label).trim(),
          })),
        },
      },
    });
    useAlert('Tampilan widget disimpan');
  } catch (error) {
    useAlert('Gagal menyimpan tampilan widget');
  }
};

watch(() => props.inbox, setDefaults);
onMounted(setDefaults);

const fieldClass =
  'w-full rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak px-3 py-2 text-sm text-n-slate-12';
</script>

<template>
  <div class="mx-6 grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8 pb-10">
    <form class="flex flex-col gap-6" @submit.prevent="updateInbox">
      <section class="flex flex-col gap-3">
        <h3 class="text-heading-3 text-n-slate-12">Gaya</h3>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          Font
          <select v-model="font" :class="fieldClass">
            <option v-for="f in FONTS" :key="f" :value="f">{{ f }}</option>
          </select>
        </label>
        <div class="flex flex-col gap-1 text-sm text-n-slate-11">
          Sudut
          <div class="flex flex-wrap gap-2">
            <button
              v-for="r in RADIUS"
              :key="r.value"
              type="button"
              class="px-3 py-1.5 text-sm outline outline-1"
              :class="
                radius === r.value
                  ? 'outline-n-brand text-n-brand'
                  : 'outline-n-weak text-n-slate-11'
              "
              :style="{ borderRadius: r.px }"
              @click="radius = r.value"
            >
              {{ r.label }}
            </button>
          </div>
        </div>
        <div class="flex flex-col gap-1 text-sm text-n-slate-11">
          Header
          <div class="flex gap-4">
            <label
              v-for="h in HEADERS"
              :key="h.value"
              class="flex items-center gap-2 text-n-slate-12"
            >
              <input v-model="header" type="radio" :value="h.value" />
              {{ h.label }}
            </label>
          </div>
        </div>
        <p class="text-xs text-n-slate-10">
          Warna, judul, dan tagline tetap diatur di tab Settings inbox ini.
        </p>
      </section>

      <section class="flex flex-col gap-3">
        <h3 class="text-heading-3 text-n-slate-12">Susunan halaman awal</h3>
        <p class="text-xs text-n-slate-10">
          Centang yang ditampilkan, atur urutannya dengan panah.
        </p>
        <div
          v-for="(block, index) in blocks"
          :key="block.key"
          class="flex items-center gap-3 rounded-lg outline outline-1 outline-n-weak px-3 py-2"
        >
          <input v-model="block.enabled" type="checkbox" />
          <span
            class="flex-1 text-sm"
            :class="block.enabled ? 'text-n-slate-12' : 'text-n-slate-10'"
          >
            {{ BLOCKS[block.key] }}
          </span>
          <button
            type="button"
            class="i-lucide-chevron-up size-4 text-n-slate-11"
            :disabled="index === 0"
            @click="moveBlock(index, -1)"
          />
          <button
            type="button"
            class="i-lucide-chevron-down size-4 text-n-slate-11"
            :disabled="index === blocks.length - 1"
            @click="moveBlock(index, 1)"
          />
        </div>
      </section>

      <section class="flex flex-col gap-3">
        <h3 class="text-heading-3 text-n-slate-12">Teks</h3>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          Label tombol mulai chat
          <input
            v-model="startLabel"
            :class="fieldClass"
            placeholder="Start conversation"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          Salam pembuka
          <textarea
            v-model="greeting"
            rows="2"
            :class="fieldClass"
            placeholder="Mis. Selamat datang! Rata-rata kami membalas dalam 5 menit."
          />
        </label>
      </section>

      <section class="flex flex-col gap-3">
        <h3 class="text-heading-3 text-n-slate-12">Tombol cepat</h3>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          Judul
          <input
            v-model="quickTitle"
            :class="fieldClass"
            placeholder="Butuh bantuan apa?"
          />
        </label>
        <p class="text-xs text-n-slate-10">
          Klik tombol = chat dibuka dan pesan terkirim otomatis. Maksimal 6.
        </p>
        <div
          v-for="(action, index) in quickActions"
          :key="index"
          class="grid grid-cols-[1fr_1.4fr_auto] gap-2 items-center"
        >
          <input v-model="action.label" :class="fieldClass" placeholder="Label" />
          <input
            v-model="action.message"
            :class="fieldClass"
            placeholder="Pesan yang dikirim"
          />
          <button
            type="button"
            class="i-lucide-trash-2 size-4 text-n-ruby-11"
            @click="removeQuickAction(index)"
          />
        </div>
        <Button
          type="button"
          variant="faded"
          color="slate"
          icon="i-lucide-plus"
          label="Tambah tombol"
          class="self-start"
          :disabled="quickActions.length >= 6"
          @click="addQuickAction"
        />
      </section>

      <Button
        type="submit"
        label="Simpan tampilan"
        class="self-start"
        :is-loading="uiFlags.isUpdating"
      />
    </form>

    <!-- Pratinjau kira-kira halaman awal widget -->
    <div class="lg:sticky lg:top-4 self-start">
      <p class="text-sm text-n-slate-11 mb-2">Pratinjau</p>
      <div
        class="w-[340px] h-[560px] overflow-hidden flex flex-col bg-[#f7f8fa] shadow-lg outline outline-1 outline-n-weak"
        :style="{ borderRadius: '16px', fontFamily: `'${font}', Inter, sans-serif` }"
      >
        <div
          class="px-5 pt-6 pb-5"
          :style="
            header === 'banner'
              ? {
                  background: widgetColor,
                  color: '#fff',
                  borderBottomLeftRadius: radiusPx,
                  borderBottomRightRadius: radiusPx,
                  paddingBottom: '2.25rem',
                }
              : { color: '#1f2937' }
          "
        >
          <p class="text-xl font-semibold">
            {{ inbox.welcome_title || inbox.name }}
          </p>
          <p class="text-sm mt-1 opacity-80">{{ inbox.welcome_tagline }}</p>
        </div>
        <div
          class="flex flex-col gap-3 px-4 pb-4 overflow-auto"
          :class="header === 'banner' ? '-mt-5' : 'mt-auto'"
        >
          <template v-for="key in previewBlocks" :key="key">
            <div
              v-if="key === 'greeting' && greeting"
              class="bg-white px-4 py-3 text-sm text-[#1f2937] shadow-sm"
              :style="{ borderRadius: radiusPx }"
            >
              {{ greeting }}
            </div>
            <div
              v-else-if="key === 'quick_actions' && filledActions.length"
              class="bg-white px-4 py-3 shadow-sm"
              :style="{ borderRadius: radiusPx }"
            >
              <p v-if="quickTitle" class="text-xs text-[#6b7280] mb-2">
                {{ quickTitle }}
              </p>
              <div class="flex flex-wrap gap-1.5">
                <span
                  v-for="a in filledActions"
                  :key="a.label"
                  class="px-2.5 py-1 text-xs border border-solid"
                  :style="{
                    color: widgetColor,
                    borderColor: widgetColor,
                    borderRadius: radiusPx,
                  }"
                >
                  {{ a.label }}
                </span>
              </div>
            </div>
            <div
              v-else-if="key === 'start'"
              class="bg-white px-4 py-3 shadow-sm"
              :style="{ borderRadius: radiusPx }"
            >
              <p class="text-sm font-medium text-[#1f2937]">We are online</p>
              <p class="text-xs text-[#6b7280]">Typically replies in a few minutes</p>
              <p class="text-sm font-medium mt-2" :style="{ color: widgetColor }">
                {{ startLabel || 'Start conversation' }} ›
              </p>
            </div>
            <div
              v-else-if="key === 'articles'"
              class="bg-white px-4 py-3 text-xs text-[#6b7280] shadow-sm"
              :style="{ borderRadius: radiusPx }"
            >
              Artikel Help Center (kalau portal terhubung)
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
